import * as types from '../mutation-types'
import form from '@/api/form'
import Vue from 'vue'

const state = {
  questions: {},
  conditionalQuestions: {},
  formInfo: null,
  questionnaireForm: {},
  activeFormQuestionIndex: 0,
  questionnaireFormCount: 0,
  activeQuestionnaire: {}
}

const getters = {
  questions: (state) => (formId) => {
    return state.questions[formId] ? state.questions[formId] : []
  },
  formInfo: (state) => {
    return state.formInfo ? state.formInfo : null
  },
  questionnaireForm: (state) => (index) => {
    return state.questionnaireForm[index] ? state.questionnaireForm[index] : null
  },
  activeFormQuestionIndex: (state) => {
    return state.activeFormQuestionIndex ? state.activeFormQuestionIndex : 0
  },
  questionnaireFormCount: (state) => {
    return state.questionnaireFormCount ? state.questionnaireFormCount : 0
  },
  activeQuestionnaire: (state) => (questionnaireId) => {
    return state.activeQuestionnaire[questionnaireId] ? state.activeQuestionnaire[questionnaireId] : []
  }
}

const actions = {
  getFormQuestions ({ commit, state }, { formId, questionnaireId }) {
    return new Promise((resolve, reject) => {
      form.get(formId, questionnaireId).then((response) => {
        const questions = response.data.result.questions
        const form = response.data.result.form
        const conditionalQuestions = response.data.result.conditionalQuestions
        commit(types.ADD_QUESTIONS, { formId: formId, questions: questions })
        commit(types.SORT_QUESTIONS, formId)
        commit(types.ADD_FORM_INFO, form)
        if (conditionalQuestions !== null) {
          conditionalQuestions.forEach((conditionalQuestion) => {
            commit(types.SET_DEPENDANT_QUESTIONS, conditionalQuestion)
          })
        }
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  saveUserFormQuestionAnswers ({ commit }, data) {
    return new Promise((resolve, reject) => {
      form.saveUserFormQuestionAnswers(data).then((response) => {
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  getQuestionnaireForm ({ commit, state }, data) {
    return new Promise((resolve, reject) => {
      form.getQuestionnaireForm(data).then((response) => {
        commit(types.CLEAR_ACTIVEQUESTIONNAIRES)
        const questionnaireFormList = response.data.result.questionnaireForm
        commit(types.TOTAL_FORM_QUESTIONNAIRE_COUNT, questionnaireFormList.length)
        questionnaireFormList.forEach((questionnaireForm, index) => {
          commit(types.ADD_QUESTIONNAIRE_INFO, { questionnaireForm, index })
        })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  clearFeatures ({ commit }) {
    commit(types.CLEAR_QUESTIONS)
  },
  setCurrentQuestionnaireFormIndex ({ commit }, index) {
    commit(types.IDENTIFY_FORM_QUESTION_INDEX, index)
  },
  setDropDownAnswer ({ commit }, { formId, questionId, answer, checkConditionalQuestion, singleSelect }) {
    return new Promise((resolve, reject) => {
      commit(types.SET_DROPDOWN_ANSWER, { formId, questionId, answer, checkConditionalQuestion, singleSelect })
    })
  },
  setStringAnswer ({ commit }, { formId, questionId, answer }) {
    return new Promise((resolve, reject) => {
      commit(types.SET_STRING_ANSWER, { formId, questionId, answer })
    })
  },
  handleMultiselectConditional ({ commit }, { formId, parentQuestion, answers }) {
    return new Promise((resolve, reject) => {
      commit(types.HANDLE_CONDITIONAL_ON_MULTISELECT, { formId, parentQuestion, answers })
    })
  }
}

const mutations = {
  [types.ADD_QUESTIONS] (state, { formId, questions }) {
    Vue.set(state.questions, formId, questions)
  },
  [types.SET_DEPENDANT_QUESTIONS] (state, conditionalQuestion) {
    if (!state.conditionalQuestions[conditionalQuestion.question_id]) {
      Vue.set(state.conditionalQuestions, conditionalQuestion.question_id, {})
    }
    Vue.set(state.conditionalQuestions[conditionalQuestion.question_id], conditionalQuestion.dependent_question_option_id, conditionalQuestion)
  },
  [types.SORT_QUESTIONS] (state, formId) {
    state.questions[formId] = state.questions[formId] ? state.questions[formId].sort((a, b) => a.sortOrder - b.sortOrder) : []
  },
  [types.ADD_FORM_INFO] (state, formInfo) {
    state.formInfo = formInfo
  },
  [types.TOTAL_FORM_QUESTIONNAIRE_COUNT] (state, count) {
    state.questionnaireFormCount = count
  },
  [types.ADD_QUESTIONNAIRE_INFO] (state, params) {
    Vue.set(state.questionnaireForm, params.index, params.questionnaireForm)
    if (!state.activeQuestionnaire[params.questionnaireForm.questionnaire_id]) {
      state.activeQuestionnaire[params.questionnaireForm.questionnaire_id] = []
    }
    state.activeQuestionnaire[params.questionnaireForm.questionnaire_id].push(params.questionnaireForm)
  },
  [types.IDENTIFY_FORM_QUESTION_INDEX] (state, index) {
    state.activeFormQuestionIndex = index
  },
  [types.CLEAR_QUESTIONS] (state) {
    state.questions = {}
  },
  [types.CLEAR_ACTIVEQUESTIONNAIRES] (state) {
    state.activeQuestionnaire = {}
  },
  [types.HANDLE_CONDITIONAL_ON_MULTISELECT] (state, { formId, parentQuestion, answers }) {
    if (Object.keys(state.conditionalQuestions).length > 0) {
      state.questions[formId].forEach(question => {
        if (question.isConditional && !parentQuestion.isConditional && state.conditionalQuestions[question.id]) {
          let matchFound = false
          answers.forEach((answer) => {
            parentQuestion.options.forEach((option) => {
              if (option.label === answer && state.conditionalQuestions[question.id][option.id] &&
                  state.conditionalQuestions[question.id][option.id].dependent_question_id === parentQuestion.id) {
                if (state.conditionalQuestions[question.id][option.id].dependent_question_answer === answer) {
                  matchFound = true
                } else {
                  question.isHidden = true
                }
              }
            })
          })
          if (question.isConditional && matchFound) {
            question.isHidden = false
          }

          if (question.isConditional && (!matchFound || answers.length === 0)) {
            question.isHidden = true
          }
        }
      })
    }
  },
  [types.SET_DROPDOWN_ANSWER] (state, { formId, questionId, answer, checkConditionalQuestion }) {
    let parentQuestion = null
    state.questions[formId].forEach(question => {
      if (question.id === questionId) {
        question.dropdownAnswer = answer
        parentQuestion = question
      }
    })

    if (checkConditionalQuestion && Object.keys(state.conditionalQuestions).length > 0 && !parentQuestion.isConditional) {
      state.questions[formId].forEach(question => {
        let matchFound = false
        if (question.isConditional) {
          if (state.conditionalQuestions[question.id] &&
              state.conditionalQuestions[question.id][answer.id] &&
              state.conditionalQuestions[question.id][answer.id].dependent_question_id === questionId) {
            matchFound = true
          }
          if (!matchFound) {
            question.isHidden = true
          } else {
            question.isHidden = false
          }
        }
      })
    }
  },
  [types.SET_STRING_ANSWER] (state, { formId, questionId, answer }) {
    state.questions[formId].forEach(question => {
      if (question.id === questionId) {
        question.userStringAnswer = answer
      }
    })
  },
  [types.RESET_ON_LOGOUT] (state) {
    state.questions = {}
    state.formInfo = null
    state.questionnaireForm = {}
    state.activeFormQuestionIndex = 0
    state.questionnaireFormCount = 0
    state.activeQuestionnaire = {}
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
